// Horizontal filmstrip of game covers used during the "variety" scroll segment.

import React from 'react';
import { ASSETS } from '@airhead-games/shared/configs';

type GameCover = {
  src: string;
  alt: string;
};

const GAME_COVERS: GameCover[] = [
  { src: ASSETS.coverMemeMash, alt: 'Meme Mash' },
  { src: ASSETS.coverTooManyCooks, alt: 'Too Many Cooks' },
  { src: ASSETS.coverWTFIsHappening, alt: 'WTF Is Happening' },
  { src: ASSETS.coverFeedTheFarm, alt: 'Feed The Farm' },
  {
    src: ASSETS.coverFinishEachOthersSandwiches,
    alt: "Finish Each Other's Sandwiches",
  },
  { src: ASSETS.coverSecondOpinions, alt: 'Second Opinions' },
  { src: ASSETS.coverBadBunnies, alt: 'Bad Bunnies' },
  { src: ASSETS.coverQuinspire, alt: 'Quinspire' },
  { src: ASSETS.coverEgoManiacs, alt: 'Ego Maniacs' },
];

type GameCoverFilmstripProps = {
  /** Optional ref attached to the strip's outer element so callers can
   *  measure its intrinsic width (used to derive scroll-driven entry/exit
   *  offsets that always start offscreen). */
  innerRef?: React.Ref<HTMLDivElement>;
};

const GameCoverFilmstrip: React.FC<GameCoverFilmstripProps> = ({
  innerRef,
}) => {
  return (
    <div
      ref={innerRef}
      className="flex h-full w-max items-center gap-6 px-8 sm:gap-7 sm:px-10 md:gap-8 md:px-12 lg:gap-9 lg:px-14 2xl:gap-10"
    >
      {GAME_COVERS.map((cover) => (
        <div
          key={cover.alt}
          // Cover sizes grow up the breakpoint ladder, but we deliberately
          // keep growth modest past xl so the strip can fit under the
          // variety hero text on tall+wide displays (1080p, 1440p) without
          // overflowing the strip's allotted band.
          //
          // Short-viewport overrides shrink covers further on 1366×768 and
          // 1280×720 laptops where the band collapses to ~200px tall.
          className={[
            'relative shrink-0 overflow-hidden rounded-2xl',
            'h-[19rem] w-[19rem]',
            'sm:h-[20rem] sm:w-[20rem]',
            'md:h-[21rem] md:w-[21rem]',
            'lg:h-[22rem] lg:w-[22rem]',
            'xl:h-[22rem] xl:w-[22rem]',
            '2xl:h-[24rem] 2xl:w-[24rem]',
            'min-[1920px]:h-[26rem] min-[1920px]:w-[26rem]',
            'min-[2560px]:h-[28rem] min-[2560px]:w-[28rem]',
            // Short-viewport caps
            '[@media(max-height:820px)]:h-[17rem] [@media(max-height:820px)]:w-[17rem]',
            '[@media(max-height:720px)]:h-[15rem] [@media(max-height:720px)]:w-[15rem]',
          ].join(' ')}
          style={{ containerType: 'inline-size' }}
        >
          <img
            src={cover.src}
            alt={cover.alt}
            className="absolute inset-0 block h-full w-full object-cover"
            loading="lazy"
          />
          <div
            className="absolute inset-0 flex items-center justify-center px-3 text-center uppercase leading-none text-white"
            style={{
              fontFamily: 'var(--font-modak)',
              fontSize: 'clamp(1.4rem, 18cqw, 2.5rem)',
              textShadow:
                '0 1px 0 rgba(0,0,0,0.45), 0 3px 9px rgba(0,0,0,0.55)',
            }}
          >
            {cover.alt}
          </div>
        </div>
      ))}
    </div>
  );
};

export default GameCoverFilmstrip;
